/**
 * ═══════════════════════════════════════════════════════════════════════════
 * QUÉ SE ADMITE COMO COMPROBANTE MÉDICO · y cuánto puede pesar
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Estas dos constantes las necesitan DOS lados que no se pueden importar entre
 * sí: `apps/api/src/almacen.ts`, que escribe en disco, y `apps/panel`, que es
 * el navegador de quien sube el papel.
 *
 * Estaban solo en el primero, y el panel tenía dos salidas malas: copiar la
 * lista —dos sitios que un día dejan de decir lo mismo— o no comprobar nada y
 * dejar que alguien suba doscientos megas para enterarse al final. Por eso
 * viven aquí, que es el único paquete que ven los dos.
 *
 * `almacen.ts` las RE-EXPORTA, así que quien ya las importaba de allí sigue
 * igual y no hay dos nombres para lo mismo.
 */

/**
 * Lo que se acepta, y por qué la lista es corta.
 *
 * Un comprobante médico llega como PDF o como foto del papel. Nada más entra:
 * cada tipo de fichero que se acepta es una forma de que algo raro acabe en
 * disco, y un `.docx` no lo manda nadie que esté mandando un informe.
 *
 * El valor es la extensión con la que se guarda. NO se usa la del nombre que
 * llega: ese nombre lo escribe quien sube, y un `informe.pdf.exe` guardado con
 * su extensión es un fichero ejecutable en el disco del servidor.
 */
export const TIPOS_DE_COMPROBANTE: Readonly<Record<string, string>> = {
  "application/pdf": ".pdf",
  "image/jpeg": ".jpg",
  "image/png": ".png",
  "image/heic": ".heic",
  "image/webp": ".webp",
};

/**
 * Diez megas.
 *
 * Una foto de un móvil moderno pesa entre dos y ocho; un PDF escaneado de dos
 * páginas, menos de uno. Diez deja pasar el peor caso normal y para el fichero
 * que no es un comprobante.
 */
export const BYTES_MAXIMOS_DE_COMPROBANTE = 10 * 1024 * 1024;

/**
 * Para el atributo `accept` del selector de ficheros.
 *
 * Es una AYUDA, no una comprobación: `accept` solo cambia lo que el diálogo
 * enseña por defecto, y en todos los navegadores se puede elegir «todos los
 * ficheros» y saltárselo. Quien decide de verdad es la API.
 */
export const ACCEPT_DE_COMPROBANTE = Object.keys(TIPOS_DE_COMPROBANTE).join(",");

/**
 * ¿Puede este fichero subirse, tal y como lo ve el navegador?
 *
 * Devuelve `null` si sí, y si no, la frase que hay que enseñar. Se contesta
 * ANTES del viaje porque las dos respuestas de la API —415 y 413— llegan
 * después de haber subido, y la de tamaño llega a veces como un fallo de red:
 * el servidor corta mientras lee, y un navegador que todavía está escribiendo
 * el cuerpo no siempre se entera de que le han contestado.
 *
 * `tipo` vacío es un caso real y no un error del usuario: algunos navegadores
 * no saben decir el tipo de un HEIC. Por eso tiene su propia frase, que dice
 * qué hacer, en vez de la lista de tipos admitidos —que incluye el HEIC y
 * dejaría a quien lo lee sin entender nada.
 */
export function loQueImpideSubirlo(fichero: {
  readonly tipo: string;
  readonly bytes: number;
}): string | null {
  if (fichero.tipo === "") {
    return (
      "Este navegador no sabe decir de qué tipo es ese fichero, así que no se puede subir. " +
      "Vuelve a guardarlo como PDF o como JPG y pruébalo otra vez."
    );
  }
  if (!(fichero.tipo in TIPOS_DE_COMPROBANTE)) {
    return `Un comprobante llega como PDF o como foto. Se admiten: ${Object.keys(TIPOS_DE_COMPROBANTE).join(", ")}.`;
  }
  if (fichero.bytes > BYTES_MAXIMOS_DE_COMPROBANTE) {
    const megas = BYTES_MAXIMOS_DE_COMPROBANTE / 1024 / 1024;
    return `El comprobante no puede pasar de ${String(megas)} MB, y ese pesa ${(fichero.bytes / 1024 / 1024).toFixed(1)} MB.`;
  }
  if (fichero.bytes === 0) {
    return "Ese fichero está vacío.";
  }
  return null;
}
